import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import Dash, { DashProps } from './Dash'
import TitleDescription, { TitleDescriptionProps } from './TitleDescription'
import SubTitleDescription, { SubTitleDescriptionProps } from './SubTitleDescription'
import EmphasizedDescription, { EmphasizedDescriptionProps } from './EmphasizedDescription'
import TitleDescriptionSmall, { TitleDescriptionSmallProps } from './TitleDescriptionSmall'
import ShowPriceDetail from './ShowPriceDetail'
import VipDiscountNotice from '~/app/components/common/VipDiscountNotice'
import styled from '~/app/components/styled'
import CouponContent, {
  CouponContentProps,
} from '~/app/components/order-list/details/InformationPanel/CouponContent'

/**
 * index component
 * author: soojin.oh
 */

export enum DataType {
  TitleDescription = 'TitleDescription',
  TitleDescriptionSmall = 'TitleDescriptionSmall',
  SubTitleDescription = 'SubTitleDescription',
  EmphasizedDescription = 'EmphasizedDescription',
  Dash = 'Dash',
  Dotted = 'Dotted',
  Custom = 'Custom',
  ShowDetail = 'ShowDetail',
  VipDiscountNotice = 'VipDiscountNotice',
  CouponContent = 'CouponContent',
}

export type Renderable =
  | string
  | number
  | ReactElement
  | ReactElement[]
  | JSX.Element
  | JSX.Element[]

export type TitleDescription = {
  type: DataType.TitleDescription
} & TitleDescriptionProps

export type TitleDescriptionSmall = {
  type: DataType.TitleDescriptionSmall
} & TitleDescriptionSmallProps

export type SubTitleDescription = {
  type: DataType.SubTitleDescription
} & SubTitleDescriptionProps

export type EmphasizedDescription = {
  type: DataType.EmphasizedDescription
} & EmphasizedDescriptionProps

export type Dash = {
  type: DataType.Dash
}
export type Dotted = {
  type: DataType.Dotted
} & DashProps

export type Custom = {
  type: DataType.Custom
  body: Renderable
}

export type ShowDetail = {
  type: DataType.ShowDetail
}

export type VipDiscountNotice = {
  type: DataType.VipDiscountNotice
  vipDiscountAmountText: string
}

export type CouponContent = {
  type: DataType.CouponContent
} & CouponContentProps

export type InformationFormat =
  | TitleDescription
  | SubTitleDescription
  | EmphasizedDescription
  | Dash
  | Dotted
  | TitleDescriptionSmall
  | Custom
  | ShowDetail
  | VipDiscountNotice
  | CouponContent
// type definitions
type InformationProps = {
  information: Array<InformationFormat>
}

const VipDiscountNoticeWrapper = styled(VipDiscountNotice)`
  position: relative;
  right: 2rem;
  top: -0.6rem;
`

// index component
const InformationPanel: React.FunctionComponent<InformationProps> = function ({ information }) {
  const [showPriceDetail, setShowPriceDetail] = useState(true)

  useEffect(() => {
    setShowPriceDetail(!information.some(info => info.type === DataType.ShowDetail))
  }, [information])

  const contents = useMemo(
    () =>
      information.map((info, index) =>
        info.type === DataType.SubTitleDescription ? (
          <SubTitleDescription {...info} key={index} isShow={showPriceDetail} />
        ) : info.type === DataType.TitleDescription ? (
          <TitleDescription {...info} key={index} />
        ) : info.type === DataType.EmphasizedDescription ? (
          <EmphasizedDescription {...info} key={index} />
        ) : info.type === DataType.ShowDetail ? (
          <ShowPriceDetail key={index} isShow={showPriceDetail} setIsShow={setShowPriceDetail} />
        ) : info.type === DataType.Dash ? (
          <Dash key={index} />
        ) : info.type === DataType.Dotted ? (
          <Dash key={index} dotted />
        ) : info.type === DataType.TitleDescriptionSmall ? (
          <TitleDescriptionSmall {...info} key={index} />
        ) : info.type === DataType.Custom ? (
          <React.Fragment key={index}>{info.body}</React.Fragment>
        ) : info.type === DataType.VipDiscountNotice ? (
          <VipDiscountNoticeWrapper
            key={index}
            totalDiscountPriceText={info.vipDiscountAmountText}
          />
        ) : info.type === DataType.CouponContent ? (
          <CouponContent key={index} isShow={showPriceDetail} {...info} />
        ) : null
      ),
    [information, showPriceDetail]
  )
  return <>{contents}</>
}

export default InformationPanel
